// eslint-disable-next-line max-len
// ref: https://github.com/ant-design/ant-design/blob/d0cc4087ddd91f069dea88572f9ba24e984e3b18/components/checkbox/Group.tsx
import type { CheckboxGroupProps } from 'antd/lib/checkbox';
import type { CheckboxOptionType, CheckboxValueType } from 'antd/lib/checkbox/Group';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import Checkbox from './Checkbox';
import GroupContext from './Context';

const Container = styled.div`
    display: flex;
    flex-direction: column;
`;

const CheckboxGroup = ({ defaultValue, children, options = [], onChange, ...restProps }: CheckboxGroupProps) => {
    const [value, setValue] = useState<CheckboxValueType[]>(restProps.value || defaultValue || []);
    const [registeredValues, setRegisteredValues] = useState<CheckboxValueType[]>([]);

    useEffect(() => {
        if ('value' in restProps) {
            setValue(restProps.value || []);
        }
    }, [restProps.value]);

    const memoOptions = useMemo(
        () =>
            options.map<CheckboxOptionType>(option => {
                if (typeof option === 'string' || typeof option === 'number') {
                    return { label: option, value: option };
                }

                return option;
            }),
        [options]
    );

    const cancelValue = (val: string) => {
        setRegisteredValues(prevValues => prevValues.filter(v => v !== val));
    };

    const registerValue = (val: string) => {
        setRegisteredValues(prevValues => [...prevValues, val]);
    };

    const toggleOption = (option: CheckboxOptionType) => {
        const optionIndex = value.indexOf(option.value);
        const newValue = [...value];

        if (optionIndex === -1) {
            newValue.push(option.value);
        } else {
            newValue.splice(optionIndex, 1);
        }

        if (!('value' in restProps)) {
            setValue(newValue);
        }

        onChange?.(
            newValue
                .filter(val => registeredValues.includes(val))
                .toSorted((a, b) => {
                    const indexA = memoOptions.findIndex(opt => opt.value === a);
                    const indexB = memoOptions.findIndex(opt => opt.value === b);

                    return indexA - indexB;
                })
        );
    };

    const childrenNode = options.length
        ? memoOptions.map<ReactNode>(option => (
              <Checkbox
                  key={option.value.toString()}
                  checked={value.includes(option.value)}
                  disabled={'disabled' in option ? option.disabled : restProps.disabled}
                  onChange={option.onChange}
                  value={option.value}
              >
                  {option.label}
              </Checkbox>
          ))
        : children;

    // eslint-disable-next-line react/jsx-no-constructed-context-values
    const context = {
        toggleOption,
        value,
        disabled: restProps.disabled,
        name: restProps.name,
        registerValue,
        cancelValue,
    };

    return (
        <Container>
            <GroupContext.Provider value={context}>{childrenNode}</GroupContext.Provider>
        </Container>
    );
};

export default CheckboxGroup;
